import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { getSessionBranchId } from "../utils/sessionBranch";
import { node_imageBaseUrl } from "./const";
import {
  verifyMissingStock,
  createMissingStock,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";
import Pagination from "../components/Pagination";

const getProductImageUrl = (rawPath) => {
  if (!rawPath || typeof rawPath !== "string") return null;
  const p = rawPath.trim();
  if (
    p.startsWith("http://") ||
    p.startsWith("https://") ||
    p.startsWith("data:")
  ) {
    return p;
  }
  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  if (!base) return `/${p.replace(/^\/+/, "")}`;
  return `${base}/${p.replace(/^\/+/, "")}`;
};

function StockCheck() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const tagInputRef = useRef(null);

  const [tagInput, setTagInput] = useState("");
  const [scannedTags, setScannedTags] = useState([]);
  const [missingItems, setMissingItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);
  const [showRemovedItems, setShowRemovedItems] = useState(false);
  const [verifySummary, setVerifySummary] = useState(null);
  const [extraTagNos, setExtraTagNos] = useState([]);
  const [scannedCount, setScannedCount] = useState(null);
  const [hasVerified, setHasVerified] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [removeModal, setRemoveModal] = useState({
    show: false,
    item: null,
    reason: "",
  });

  const handlePageChange = (newPage) => {
    const totalPages = Math.max(1, Math.ceil(missingItems.length / itemsPerPage));
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const displayedMissingItems = missingItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  useEffect(() => {
    tagInputRef.current?.focus();
  }, []);

  const addTag = (raw) => {
    const tag = String(raw || "").trim();
    if (!tag) return;
    setScannedTags((prev) => {
      if (prev.some((t) => t.toLowerCase() === tag.toLowerCase())) {
        toast.error(`Tag "${tag}" is already scanned`);
        return prev;
      }
      return [...prev, tag];
    });
    setTagInput("");
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  const removeScannedTag = (tag) => {
    setScannedTags((prev) => prev.filter((t) => t !== tag));
  };

  const handleVerify = async () => {
    if (scannedTags.length === 0) {
      toast.error("Scan at least one tag before verifying");
      return;
    }

    const branch_id = getSessionBranchId();
    if (branch_id == null) {
      toast.error("Branch not set in session");
      return;
    }

    setVerifying(true);
    try {
      const response = await verifyMissingStock({
        branch_id,
        tag_nos: scannedTags,
      });

      if (!isErpApiSuccess(response)) {
        toast.error(response?.message || "Verification failed");
        return;
      }

      const data = response?.data || {};
      setMissingItems(Array.isArray(data.missing_items) ? data.missing_items : []);
      setCurrentPage(1);
      setExtraTagNos(
        Array.isArray(data.extra_tag_nos) ? data.extra_tag_nos : [],
      );
      setScannedCount(
        data.scanned_count != null ? Number(data.scanned_count) : scannedTags.length,
      );
      setVerifySummary({
        scanned_count: data.scanned_count ?? scannedTags.length,
        in_stock_count: data.in_stock_count ?? 0,
        missing_count: data.missing_count ?? 0,
      });
      setRemovedItems([]);
      setHasVerified(true);
      toast.success(response?.message || "Verification complete");
    } catch (error) {
      console.error("Stock check verify failed:", error);
      toast.error(getErpRequestErrorMessage(error, "Verification failed"));
    } finally {
      setVerifying(false);
    }
  };

  const openRemoveModal = (item) => {
    setRemoveModal({ show: true, item, reason: "" });
  };

  const closeRemoveModal = () => {
    setRemoveModal({ show: false, item: null, reason: "" });
  };

  const confirmRemoveMissing = () => {
    const reason = String(removeModal.reason || "").trim();
    if (!reason) {
      toast.error("Removal reason is required");
      return;
    }

    const item = removeModal.item;
    if (!item?.tag_no) {
      closeRemoveModal();
      return;
    }

    setMissingItems((prev) => {
      const next = prev.filter((row) => row.tag_no !== item.tag_no);
      const totalPages = Math.max(1, Math.ceil(next.length / itemsPerPage));
      setCurrentPage((page) => Math.min(page, totalPages));
      return next;
    });
    setRemovedItems((prev) => [
      ...prev,
      {
        tag_no: item.tag_no,
        reason,
        removed_at: new Date().toISOString(),
      },
    ]);
    closeRemoveModal();
  };

  const buildRemarksPayload = () => {
    const base = String(remarks || "").trim();
    const removalNotes = removedItems.map(
      (r) => `Removed ${r.tag_no}: ${r.reason}`,
    );
    if (removalNotes.length === 0) return base;
    const suffix = removalNotes.join(" | ");
    return base ? `${base} | ${suffix}` : suffix;
  };

  const resetForm = () => {
    setTagInput("");
    setScannedTags([]);
    setMissingItems([]);
    setCurrentPage(1);
    setRemovedItems([]);
    setShowRemovedItems(false);
    setVerifySummary(null);
    setExtraTagNos([]);
    setScannedCount(null);
    setHasVerified(false);
    setRemarks("");
    tagInputRef.current?.focus();
  };

  const handleSave = async () => {
    if (!hasVerified || scannedCount == null) {
      toast.error("Run verification before saving");
      return;
    }

    const branch_id = getSessionBranchId();
    if (branch_id == null) {
      toast.error("Branch not set in session");
      return;
    }

    const missing_tag_nos = missingItems.map((item) => item.tag_no);

    setSaving(true);
    try {
      const response = await createMissingStock({
        branch_id,
        scanned_count: scannedCount,
        missing_tag_nos,
        remarks: buildRemarksPayload(),
      });

      if (!isErpApiSuccess(response)) {
        toast.error(response?.message || "Failed to save stock check");
        return;
      }

      toast.success(response?.message || "Stock check saved");
      navigate("/productassortingtable");
    } catch (error) {
      console.error("Stock check save failed:", error);
      toast.error(getErpRequestErrorMessage(error, "Failed to save stock check"));
    } finally {
      setSaving(false);
    }
  };

  const canSave = hasVerified && scannedCount != null && !saving;

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="card-title mb-0">Stock Check</h4>
              <p className="text-muted small mb-0 mt-1">
                Scan tags and verify against current stock
              </p>
            </div>
            <Button
              variant="outline-secondary"
              className={buttonClass}
              onClick={() => navigate("/productassortingtable")}
            >
              <Icon icon="solar:arrow-left-linear" className="me-1" />
              Back
            </Button>
          </div>

          <div className="card-body p-4">
            {/* Scan / input section */}
            <section className="mb-4">
              <Form.Label className="fw-semibold">Scan tags</Form.Label>
              <div className="d-flex flex-column flex-md-row gap-2 mb-3">
                <Form.Control
                  ref={tagInputRef}
                  type="text"
                  className={buttonClass}
                  placeholder="Scan or enter tag number and press Enter"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  autoComplete="off"
                  style={{ minHeight: "48px" }}
                />
                <Button
                  variant="primary"
                  className={`${buttonClass} flex-shrink-0`}
                  onClick={handleVerify}
                  disabled={verifying || scannedTags.length === 0}
                  style={{ minHeight: "48px", minWidth: "120px" }}
                >
                  {verifying ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Verifying...
                    </>
                  ) : (
                    "Verify"
                  )}
                </Button>
              </div>

              {scannedTags.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {scannedTags.map((tag) => (
                    <span
                      key={tag}
                      className="badge bg-light text-dark border d-inline-flex align-items-center gap-1"
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "normal",
                        padding: "0.5rem 0.65rem",
                      }}
                    >
                      {tag}
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-danger"
                        aria-label={`Remove ${tag}`}
                        onClick={() => removeScannedTag(tag)}
                        style={{ lineHeight: 1, fontSize: "1rem" }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
            </section>

            {/* Verify summary & warnings */}
            {hasVerified && verifySummary ? (
              <div
                className={`alert alert-info py-2 mb-4 ${buttonClass}`}
                role="status"
              >
                <strong>Summary:</strong> Scanned: {verifySummary.scanned_count}{" "}
                | In Stock: {verifySummary.in_stock_count} | Missing:{" "}
                {verifySummary.missing_count}
              </div>
            ) : null}

            {extraTagNos.length > 0 ? (
              <div
                className={`alert alert-warning py-2 mb-4 ${buttonClass}`}
                role="alert"
              >
                <strong>Warning:</strong> These scanned tags are not in current
                stock: {extraTagNos.join(", ")}
              </div>
            ) : null}

            {/* Missing products */}
            {hasVerified ? (
              <section className="mb-4">
                <h5 className="mb-3">Missing Products</h5>
                <div className={`table-responsive border ${cardClass}`}>
                  <table
                    className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                  >
                    <thead className="text-dark fs-4">
                      <tr>
                        <th style={{ width: "20%" }}>Tag No</th>
                        <th style={{ width: "35%" }}>Product Group</th>
                        <th style={{ width: "25%" }}>Image</th>
                        <th style={{ width: "20%" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedMissingItems.length > 0 ? (
                        displayedMissingItems.map((item) => {
                          const imageUrl = getProductImageUrl(item.image);
                          return (
                            <tr key={item.tag_no}>
                              <td>{item.tag_no}</td>
                              <td>{item.product_group_name || "—"}</td>
                              <td>
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={item.product_group_name || item.tag_no}
                                    style={{
                                      width: "56px",
                                      height: "56px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                      border: "1px solid #dee2e6",
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                      const placeholder =
                                        e.currentTarget.nextElementSibling;
                                      if (placeholder) {
                                        placeholder.style.display = "flex";
                                      }
                                    }}
                                  />
                                ) : null}
                                <div
                                  className="bg-light text-muted d-flex align-items-center justify-content-center"
                                  style={{
                                    width: "56px",
                                    height: "56px",
                                    borderRadius: "4px",
                                    border: "1px solid #dee2e6",
                                    fontSize: "0.7rem",
                                    display: imageUrl ? "none" : "flex",
                                  }}
                                >
                                  No image
                                </div>
                              </td>
                              <td>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className={buttonClass}
                                  onClick={() => openRemoveModal(item)}
                                >
                                  Remove
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center text-muted">
                            No missing products — all in-stock tags were scanned.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  totalItems={missingItems.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  itemsPerPageOptions={[10, 20, 30, 50, 100]}
                />
              </section>
            ) : null}

            {/* Removed items audit */}
            {removedItems.length > 0 ? (
              <section className="mb-4">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none"
                  onClick={() => setShowRemovedItems((v) => !v)}
                >
                  {showRemovedItems ? "Hide" : "Show"} removed items (
                  {removedItems.length})
                </button>
                {showRemovedItems ? (
                  <ul className="list-unstyled mt-2 mb-0 small text-muted">
                    {removedItems.map((item) => (
                      <li key={`${item.tag_no}-${item.removed_at}`}>
                        <strong>{item.tag_no}</strong>: {item.reason}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ) : null}

            {/* Remarks & save — only after verify */}
            {hasVerified ? (
              <section className="border-top pt-4">
                <Row className="g-3 align-items-end">
                  <Col md={8}>
                    <Form.Label>Remarks (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      className={buttonClass}
                      placeholder="e.g. Evening verification"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    />
                  </Col>
                  <Col md={4} className="d-flex gap-2 justify-content-md-end">
                    <Button
                      variant="outline-secondary"
                      className={buttonClass}
                      onClick={resetForm}
                      disabled={saving}
                    >
                      New check
                    </Button>
                    <Button
                      variant="primary"
                      className={buttonClass}
                      onClick={handleSave}
                      disabled={!canSave}
                      style={{ minWidth: "100px" }}
                    >
                      {saving ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Saving...
                        </>
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </Col>
                </Row>
              </section>
            ) : null}
          </div>
        </div>
      </div>

      <Modal show={removeModal.show} onHide={closeRemoveModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove missing tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3">
            Tag <strong>{removeModal.item?.tag_no}</strong> will be removed from
            the missing list for this check.
          </p>
          <Form.Group>
            <Form.Label>
              Why are you removing this tag from missing list?{" "}
              <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className={buttonClass}
              value={removeModal.reason}
              onChange={(e) =>
                setRemoveModal((prev) => ({
                  ...prev,
                  reason: e.target.value,
                }))
              }
              placeholder="Enter reason..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className={buttonClass}
            onClick={closeRemoveModal}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className={buttonClass}
            onClick={confirmRemoveMissing}
          >
            Confirm remove
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default StockCheck;
